import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, ExternalLink } from 'lucide-react';
import { useAuthStore } from '../../store/useStore.js';

export default function MagicLinkSent({ email, devToken, onVerified }) {
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const { verifyMagicLink, isLoading } = useAuthStore();
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleDevLogin = async () => {
    if (!devToken) return;
    
    setError('');
    try {
      await verifyMagicLink(devToken);
      onVerified();
    } catch (error) {
      console.error('Dev login failed:', error);
      setError(error.message || 'Login failed. Please try requesting a new magic link.');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Check Your Email</h1>
          <p className="text-gray-600">
            {email && email !== 'your email' ? (
              <>We sent a magic link to <strong>{email}</strong></>
            ) : (
              'We sent a magic link to your email address'
            )}
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">What to do next:</p>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• Check your email inbox</li>
                  <li>• Click the "Log in to AvRi" link</li>
                  <li>• You'll be automatically signed in</li>
                </ul>
              </div>
            </div>
          </div>

          {timeLeft > 0 && (
            <div className="text-center text-sm text-gray-600">
              Link expires in {formatTime(timeLeft)}
            </div>
          )}

          {devToken && (
            <div className="border-t pt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Development Mode:</strong> Click below to login instantly (this won't appear in production)
                </p>
              </div>
              <button
                onClick={handleDevLogin}
                disabled={isLoading}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Dev Login (Skip Email)
              </button>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <button 
            onClick={() => window.location.reload()}
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            Didn't get the email? Try again
          </button>
        </div>
      </div>
    </div>
  );
}
